import { StateService } from "@uirouter/angular";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnInit, TemplateRef } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NEXT_TOP_NAV_BAR_PROPS } from "@webapp/navigation/components/navigation/config";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { SearchFacetsOptions } from "@webapp/search/models/search.models";
import { SubTitle } from "./models/top-nav-bar.models";
import { actionHandlerGenerator } from "./utils/action-handler";
import { setTopNavBarProps } from "./utils/top-nav-bar.utils";

@UntilDestroy()
@Component({
  selector: "top-nav-bar",
  templateUrl: "./top-nav-bar.component.html",
  styleUrls: ["./top-nav-bar.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopNavBarComponent implements OnInit, AfterViewInit {
  @Input()
  public navTitle: string | TemplateRef<unknown>;

  @Input()
  public navSubTitle: SubTitle;

  @Input()
  public showFavIcon = false;

  @Input()
  public showTabs = false;

  @Input()
  public showDropdown = false;

  @Input()
  public showButtons = false;

  @Input()
  public hideGlobalSearch = false;

  @Input()
  public isGrayBackground = false;

  @Input()
  public searchFacet: SearchFacetsOptions;

  public isNavExpanded = false;

  public actionHandler: () => void;
  /**
   * Buttons mode - relaxed is the default, compact is when element clientWidth is <= 768 (buttonsRelaxedModeBreakpoint)
   */
  public buttonsRelaxedMode: boolean = true;
  public shouldHideEverythingButSearchAndTitle: boolean = false;

  private isSearchOpen: boolean = false;
  private readonly buttonsRelaxedModeBreakpoint: number = 768;

  constructor(
    private isNavExpandedCache: IsNavigationExpandedCache,
    private cdr: ChangeDetectorRef,
    private stateService: StateService,
    private elRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit(): void {
    setTopNavBarProps({ height: NEXT_TOP_NAV_BAR_PROPS.height + "px", borderColor: NEXT_TOP_NAV_BAR_PROPS.borderColor });

    this.isNavExpandedCache
      .get$()
      .pipe(untilDestroyed(this))
      .subscribe((isNavExpanded) => {
        this.isNavExpanded = isNavExpanded;
        this.cdr.markForCheck();
      });

    if (this.navSubTitle?.action) {
      this.actionHandler = actionHandlerGenerator(this.navSubTitle.action, { stateService: this.stateService, window });
    }
  }

  public ngAfterViewInit(): void {
    this.onResize();
  }

  public onGlobalSearchToggle(isSearchOpened: boolean): void {
    this.isSearchOpen = isSearchOpened;
    this.cdr.detectChanges(); // need to detectChanges so the display is updated, otherwise the check for element overflowing will always give the same value
    this.setViewModeRules();
  }

  @HostBinding("class.gray-background")
  public get backgroundClass(): boolean {
    return this.isGrayBackground;
  }

  @HostListener("window:resize", ["$event"])
  public onResize(): void {
    this.buttonsRelaxedMode = this.topNavBar.clientWidth > this.buttonsRelaxedModeBreakpoint;
    this.setViewModeRules();
  }

  private setViewModeRules(): void {
    const { clientWidth, clientHeight, scrollWidth, scrollHeight } = this.topNavBar;
    const isTopNavBarOverflowing = scrollHeight > clientHeight || scrollWidth > clientWidth;
    this.shouldHideEverythingButSearchAndTitle = this.isSearchOpen && isTopNavBarOverflowing;
    this.cdr.detectChanges();
  }

  private get topNavBar(): HTMLElement {
    return this.elRef.nativeElement.querySelector<HTMLElement>(".top-nav-bar");
  }
}
